/* eslint-disable import/no-import-module-exports */
import React from "react";
import { hydrateRoot } from "react-dom/client";
import { loadableReady } from "@loadable/component";
import { createInstance } from "@optimizely/react-sdk";
import createStore from "store/create";
import initialize from "utils/app/initialize";
import { setTraceId, traceError } from "utils/traceContext";
import { applyForcedDecisions } from "lib/applyForcedDecisions";
import App from "containers/App";

// Create the store from the state injected via the server-generated HTML

const decodedState = globalThis.__PRELOADED_STATE__
	? JSON.parse(atob(globalThis.__PRELOADED_STATE__, "base64"))
	: null;

const store = createStore(decodedState);

if (decodedState?.app?.trackingId) {
	setTraceId(decodedState.app.trackingId);
}

const datafileFromServer = window.__OPTIMIZELY_DATAFILE__;
const userFromServer = window.__OPTIMIZELY_USER__;

let optimizelyClient = null;
if (datafileFromServer) {
	try {
		optimizelyClient = createInstance({
			datafile: datafileFromServer
		});
	} catch (err) {
		traceError("[Optimizely] Client initialization error", err);
	}
}

// Initialize segment
initialize(store);

// Allow the passed state to be garbage-collected
delete globalThis.__PRELOADED_STATE__;

// remove the `#preloaded-state` element
const preloadedStateElement = document.getElementById("preloadedState");
if (preloadedStateElement) {
	preloadedStateElement.remove();
}

const appContainer = document.getElementById("app");
let appRoot = null;

const render = (AppComponent) => {
	const element = (
		<AppComponent
			store={store}
			optimizelyClient={optimizelyClient}
			optimizelyUser={userFromServer}
		/>
	);
	if (appRoot) {
		appRoot.render(element);
	} else {
		appRoot = hydrateRoot(appContainer, element);
	}
};

(async () => {
	if (optimizelyClient) {
		try {
			const userId = userFromServer?.id || store.getState().user.visitId;
			optimizelyClient.setUser({
				id: userId,
				attributes: userFromServer?.attributes
			});

			// Must run after setUser() above - see applyForcedDecisions.js.
			applyForcedDecisions(
				optimizelyClient,
				userFromServer?.forcedDecisions,
				(flagKey, err) =>
					traceError(
						`[Optimizely] Failed to set forced decision for ${flagKey}`,
						err
					)
			);

			await optimizelyClient.onReady({ timeout: 1000 });
		} catch (err) {
			traceError(
				"[Optimizely] SDK ready timeout, hydrating anyway",
				err
			);
		}
	}

	loadableReady(() => render(App));
})();

if (module.hot) {
	module.hot.accept(["./containers/App"], () => {
		const NextApp =
			require("./containers/App").default; /* eslint-disable-line global-require, max-len */
		render(NextApp);
	});
}
